export type UserProfile = 'admin' | 'researcher'

export interface AppSession {
  profile: UserProfile
  userId: string
  email: string
  userName: string
  userRole?: string
  researcherId?: string | null
  researcherName?: string | null
  loginAt: string
  rememberDevice?: boolean
}

export interface RememberedAccess {
  profile: UserProfile
  email?: string
  userName?: string
  researcherName?: string | null
  lastLoginAt: string
}

export interface LocalCredentialFields {
  authProvider?: 'local' | 'google'
  googleLinked?: boolean
  passwordHash?: string
  passwordSalt?: string
  mustResetPassword?: boolean
  passwordUpdatedAt?: string
}

export interface LocalPasswordOverride extends Required<LocalCredentialFields> {
  profile: UserProfile
  email: string
  savedAt: string
}

export const SESSION_KEY = 'app_dna_session'
export const REMEMBERED_ACCESS_KEY = 'app_dna_remembered_access'
export const LOCAL_PASSWORD_OVERRIDES_KEY = 'app_dna_local_password_overrides'
export const DEFAULT_LOCAL_PASSWORD = '123456'
export const DEFAULT_ADMIN_EMAIL = 'admin@dna.local'
export const DEFAULT_ADMIN_NAME = 'Administrador DNA'

export const normalizeEmail = (email: string) => email.trim().toLowerCase()

const isValidProfile = (profile: unknown): profile is UserProfile =>
  profile === 'admin' || profile === 'researcher'

export const readSession = (): AppSession | null => {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(SESSION_KEY)
    if (!raw) return null

    const session = JSON.parse(raw) as AppSession
    if (!isValidProfile(session.profile) || !session.userId || !session.email || !session.userName) {
      return null
    }

    return session
  } catch (error) {
    console.error('Erro ao ler sessao local:', error)
    return null
  }
}

export const readRememberedAccess = (): RememberedAccess | null => {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(REMEMBERED_ACCESS_KEY)
    if (!raw) return null

    const remembered = JSON.parse(raw) as RememberedAccess
    if (!isValidProfile(remembered.profile) || !remembered.lastLoginAt) {
      return null
    }

    return remembered
  } catch (error) {
    console.error('Erro ao ler acesso lembrado:', error)
    return null
  }
}

export const saveRememberedAccess = (remembered: RememberedAccess) => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(REMEMBERED_ACCESS_KEY, JSON.stringify(remembered))
}

export const saveSession = (session: AppSession, rememberDevice = false) => {
  if (typeof window === 'undefined') return

  const nextSession = {
    ...session,
    rememberDevice,
  }

  window.localStorage.setItem(SESSION_KEY, JSON.stringify(nextSession))
  saveRememberedAccess({
    profile: session.profile,
    email: rememberDevice ? session.email : undefined,
    userName: rememberDevice ? session.userName : undefined,
    researcherName: rememberDevice ? session.researcherName : undefined,
    lastLoginAt: session.loginAt,
  })
}

export const clearSession = (options?: { forgetRememberedAccess?: boolean }) => {
  if (typeof window === 'undefined') return

  window.localStorage.removeItem(SESSION_KEY)

  if (options?.forgetRememberedAccess) {
    window.localStorage.removeItem(REMEMBERED_ACCESS_KEY)
  }
}

const getLocalPasswordOverrideKey = (profile: UserProfile, email: string) =>
  `${profile}:${normalizeEmail(email)}`

const readLocalPasswordOverrides = (): Record<string, LocalPasswordOverride> => {
  if (typeof window === 'undefined') return {}

  try {
    const raw = window.localStorage.getItem(LOCAL_PASSWORD_OVERRIDES_KEY)
    if (!raw) return {}

    return JSON.parse(raw) as Record<string, LocalPasswordOverride>
  } catch (error) {
    console.error('Erro ao ler senhas locais de contingencia:', error)
    return {}
  }
}

export const readLocalPasswordOverride = (
  profile: UserProfile,
  email: string
): LocalPasswordOverride | null => {
  const normalizedEmail = normalizeEmail(email)
  if (!normalizedEmail) return null

  const override = readLocalPasswordOverrides()[getLocalPasswordOverrideKey(profile, normalizedEmail)]
  if (!override?.passwordHash || !override.passwordSalt) return null

  return override
}

export const saveLocalPasswordOverride = (
  profile: UserProfile,
  email: string,
  credentials: Required<LocalCredentialFields>
) => {
  if (typeof window === 'undefined') return

  const normalizedEmail = normalizeEmail(email)
  if (!normalizedEmail) return

  const overrides = readLocalPasswordOverrides()

  overrides[getLocalPasswordOverrideKey(profile, normalizedEmail)] = {
    ...credentials,
    profile,
    email: normalizedEmail,
    mustResetPassword: false,
    savedAt: new Date().toISOString(),
  }

  window.localStorage.setItem(LOCAL_PASSWORD_OVERRIDES_KEY, JSON.stringify(overrides))
}

export const clearLocalPasswordOverride = (profile: UserProfile, email: string) => {
  if (typeof window === 'undefined') return

  const normalizedEmail = normalizeEmail(email)
  if (!normalizedEmail) return

  const overrides = readLocalPasswordOverrides()
  delete overrides[getLocalPasswordOverrideKey(profile, normalizedEmail)]
  window.localStorage.setItem(LOCAL_PASSWORD_OVERRIDES_KEY, JSON.stringify(overrides))
}

const bytesToHex = (buffer: ArrayBuffer) => (
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
)

export const createPasswordSalt = () => {
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    const randomBytes = new Uint8Array(16)
    window.crypto.getRandomValues(randomBytes)
    return Array.from(randomBytes)
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export const hashPassword = async (password: string, salt: string) => {
  const value = `${salt}:${password}`

  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    const encoded = new TextEncoder().encode(value)
    const digest = await window.crypto.subtle.digest('SHA-256', encoded)
    return bytesToHex(digest)
  }

  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(index)
    hash |= 0
  }

  return Math.abs(hash).toString(16)
}

export const createLocalCredentials = async (
  password = DEFAULT_LOCAL_PASSWORD,
  options?: { mustResetPassword?: boolean }
): Promise<Required<LocalCredentialFields>> => {
  const passwordSalt = createPasswordSalt()

  return {
    authProvider: 'local',
    googleLinked: false,
    passwordSalt,
    passwordHash: await hashPassword(password, passwordSalt),
    mustResetPassword: options?.mustResetPassword ?? password === DEFAULT_LOCAL_PASSWORD,
    passwordUpdatedAt: new Date().toISOString(),
  }
}

export const verifyLocalPassword = async (
  password: string,
  credentials: Pick<LocalCredentialFields, 'passwordHash' | 'passwordSalt'>
) => {
  if (!credentials.passwordHash || !credentials.passwordSalt) return false
  return (await hashPassword(password, credentials.passwordSalt)) === credentials.passwordHash
}

export const getLocalAdminSession = async (email: string, password: string): Promise<AppSession | null> => {
  const normalizedEmail = normalizeEmail(email)
  if (normalizedEmail !== DEFAULT_ADMIN_EMAIL) return null

  const adminPasswordHash = await hashPassword(DEFAULT_LOCAL_PASSWORD, DEFAULT_ADMIN_EMAIL)
  const inputPasswordHash = await hashPassword(password, DEFAULT_ADMIN_EMAIL)
  if (inputPasswordHash !== adminPasswordHash) return null

  return {
    profile: 'admin',
    userId: 'local-admin',
    email: DEFAULT_ADMIN_EMAIL,
    userName: DEFAULT_ADMIN_NAME,
    userRole: 'admin',
    researcherId: null,
    researcherName: null,
    loginAt: new Date().toISOString(),
  }
}
